"use client";

import { motion } from "framer-motion";
import {
  ArrowLeftRight,
  CreditCard,
  Wifi,
  Activity,
  Plus,
  LayoutGrid,
  Eye,
  ShieldCheck,
  Zap,
  Globe,
  TrendingUp,
} from "lucide-react";
import Link from "next/link";
import { useQrPopupStore } from "@/store/useQrPopupStore";

const productPills = [
  { icon: ArrowLeftRight, label: "Money transfers" },
  { icon: CreditCard, label: "Payments" },
  { icon: Wifi, label: "Travel eSIM" },
  { icon: Activity, label: "Rate tracker" },
  { icon: Plus, label: "More coming" },
];

const heroStats = [
  { value: "1B", label: "Processed" },
  { value: "28+", label: "Currencies" },
  { value: "11+", label: "Regions" },
  { value: "4.8★", label: "App store rating" },
];

const vellValues = [
  {
    icon: LayoutGrid,
    title: "One app, everything",
    description:
      "Transfers, eSIM, payments, and more — all under one roof, one login, one experience.",
  },
  {
    icon: Eye,
    title: "Radical transparency",
    description:
      "The rate you see is the rate you get. No markup buried in the exchange rate, no surprise fees at the end.",
  },
  {
    icon: ShieldCheck,
    title: "Built to be trusted",
    description:
      "Regulated across all operating markets. Bank-grade security behind a consumer-grade experience.",
  },
  {
    icon: Zap,
    title: "Speed as a default",
    description:
      "Most transfers in minutes. Real-time tracking. 24/7 availability. Fast is the baseline, not the premium.",
  },
  {
    icon: Globe,
    title: "Globally minded",
    description:
      "Built for people whose lives span multiple countries, currencies, and time zones — not just occasional travellers.",
  },
  {
    icon: TrendingUp,
    title: "Built to grow with you",
    description:
      "Whether you're sending your first transfer or managing multi-currency finances, Vell scales with your life.",
  },
];

const growthStats = [
  { value: "$1B", label: "Processed" },
  { value: "28+", label: "Currencies supported" },
  { value: "160+", label: "eSIM destinations" },
  { value: "4.8", label: "App store rating" },
];

const timeline = [
  {
    year: "2021",
    title: "Founded",
    description:
      "Two colleagues frustrated by expensive international transfers launched Vell with one mission — transfers at the real exchange rate, no markup.",
    isNow: false,
  },
  {
    year: "2022",
    title: "Regulatory licence",
    description:
      "Granted our payment institution licence, becoming fully regulated to operate across our core markets.",
    isNow: false,
  },
  {
    year: "2023",
    title: "App launch",
    description:
      "Shipped the Vell consumer app to iOS and Android, reaching our first 100,000 users in under a year.",
    isNow: false,
  },
  {
    year: "2024",
    title: "Travel eSIM launch",
    description:
      "Added travel eSIM plans to the platform — the first step toward a true financial super app.",
    isNow: false,
  },
  {
    year: "NOW",
    title: "Building the super app",
    description:
      "Expanding our product suite with payments, savings tools, and more — all inside a single account.",
    isNow: true,
  },
];

const teamMembers = [
  {
    // initials: "SC",
    name: "Hamish Benjamin",
    role: "Co-founder & CEO",
    bio: "Leads the company's vision, growth, and market expansion. Drawing on years of experience operating businesses in foreign exchange and payments industry.",
  },
  {
    // initials: "MO",
    name: "Rowena Kee",
    role: "Co-founder & CSO",
    bio: "Leads the company's product strategy, operational design, and long-term growth initiatives. Focused on building scalable systems and experiences that simplify global financial services.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};


export default function AboutUsPage() {
  const { openPopup } = useQrPopupStore();

  return (
    <div className="bg-brand-text text-brand-card">
      <section className="px-6 pt-24 pb-16 sm:pt-32 sm:pb-24">
        <div className="mx-auto lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px]">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div variants={fadeUp} className="mb-8">
              <span className="inline-flex items-center rounded-full border border-brand-card/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-muted 3xl:text-base">
                Our story
              </span>
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="text-5xl sm:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight text-brand-card max-w-4xl mb-6 3xl:text-8xl"
            >
              One app for{" "}
              <em className="font-normal italic text-brand-muted">everything</em>{" "}
              your financial life needs
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="max-w-xl text-lg leading-relaxed text-brand-card/70 mb-10 3xl:text-2xl"
            >
              Vell is the financial super app built for people who live, earn,
              travel, and spend across borders. One account. Every tool you need.
              No app-switching, no hidden fees, no compromises.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mb-14">
              {productPills.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 bg-brand-grey/30 rounded-full border border-brand-card/15 px-3.5 py-1.5 text-sm text-brand-card 3xl:text-xl"
                >
                  <Icon className="h-3.5 w-3.5 text-brand-muted text-brand-card" />
                  {label}
                </span>
              ))}
            </motion.div>
            <motion.div
              variants={fadeUp}
              className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-brand-card/10 global-border-radius overflow-hidden border border-brand-card/10"
            >
              {heroStats.map(({ value, label }) => (
                <div key={label} className="bg-brand-text px-6 py-8 3xl:px-8 3xl:py-10">
                  <p className="text-4xl font-bold text-brand-card 3xl:text-5xl">{value}</p>
                  <p className="mt-1 text-sm text-brand-muted 3xl:text-base">{label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
      <section className="border-t border-brand-card/10 px-6 py-20 sm:py-28">
        <div className="mx-auto lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px]">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-20 mb-16">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-brand-muted 3xl:text-base">
                What we&apos;re building
              </p>
              <h2 className="text-4xl sm:text-5xl font-bold leading-tight text-brand-card 3xl:text-[5rem]">
                Beyond transfers
              </h2>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="space-y-5 lg:pt-14"
            >
              <p className="text-lg leading-relaxed text-brand-card/70 3xl:text-2xl">
                People today juggle five apps to manage their financial life — one
                for transfers, one for travel money, one for their SIM abroad, one
                for savings. It&apos;s messy, expensive, and outdated.
              </p>
              <p className="text-lg leading-relaxed text-brand-card/70 3xl:text-2xl">
                Vell&apos;s answer is one app that brings it all together. We&apos;re
                building the financial layer for people who don&apos;t live in one
                place, speak one currency, or think in one market.
              </p>
            </motion.div>
          </div>

          {/* Value cards */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-card/10 global-border-radius overflow-hidden border border-brand-card/10"
          >
            {vellValues.map(({ icon: Icon, title, description }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                className="bg-brand-text p-8 3xl:p-10"
              >
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center global-border-radius bg-brand-card/8">
                  <Icon className="h-5 w-5 text-brand-card 3xl:h-6 3xl:w-6" />
                </div>
                <h3 className="mb-2 font-semibold text-brand-card 3xl:text-[2rem]">{title}</h3>
                <p className="text-sm leading-relaxed text-brand-card 3xl:text-xl">
                  {description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      <section className="border-t border-brand-card/10 px-6 py-20 sm:py-28">
        <div className="mx-auto lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px]">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-24">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="lg:sticky lg:top-32 lg:self-start"
            >
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-brand-muted 3xl:text-base">
                Our journey
              </p>
              <h2 className="text-4xl sm:text-5xl font-bold leading-tight text-brand-card mb-6 3xl:text-[5rem]">
                Started with transfers. Building toward everything.
              </h2>
              {/* <p className="text-lg leading-relaxed text-brand-card/70 3xl:text-2xl">
                Vell launched to solve a specific pain — expensive, slow
                cross-border transfers. We proved the model, earned trust in our
                first markets, and are now expanding into a full financial super
                app for globally-minded people.
              </p> */}
            </motion.div>

            {/* Right: timeline */}
            {/* <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={stagger}
              className="relative"
            >
              <div className="absolute left-[6px] top-2 bottom-2 w-px bg-brand-card/15" />
              <div className="space-y-10">
                {timeline.map(({ year, title, description, isNow }) => (
                  <motion.div
                    key={year}
                    variants={fadeUp}
                    className="relative pl-8"
                  >
                    <div
                      className={`absolute left-0 top-[5px] h-3 w-3 rounded-full border-2 ${
                        isNow
                          ? "bg-brand-card border-brand-card"
                          : "bg-brand-text border-brand-card/30"
                      }`}
                    />
                    <p
                      className={`mb-1 text-xs font-bold uppercase tracking-widest 3xl:text-2xl ${
                        isNow ? "text-brand-card" : "text-brand-muted"
                      }`}
                    >
                      {year}
                    </p>
                    <h3 className="text-base font-semibold text-brand-card mb-1.5 3xl:text-[2rem]">
                      {title}
                    </h3>
                    <p className="text-sm leading-relaxed text-brand-card/60 3xl:text-2xl">
                      {description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div> */}
          </div>
        </div>
      </section>
      <section className="border-t border-brand-card/10 bg-brand-grey/10 px-6 py-20 sm:py-28">
        <div className="mx-auto lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px]">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-20 mb-14">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-brand-muted 3xl:text-base">
                The team
              </p>
              <h2 className="text-4xl sm:text-5xl font-bold leading-tight text-brand-card 3xl:text-[5rem]">
                The people building it
              </h2>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="lg:pt-14"
            >
              <p className="text-lg leading-relaxed text-brand-card/70 3xl:text-2xl">
                We&apos;re a team of fintech builders, former bankers, and
                globally-mobile people who got tired of paying too much to move
                their own money. So we built something better.
              </p>
            </motion.div>
          </div>

          {/* Cards */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {teamMembers.map(({  name, role, bio }) => (
              <motion.div
                key={name}
                variants={fadeUp}
                className="global-border-radius bg-brand-card/5 p-6 3xl:p-8"
              >
                <h3 className="font-semibold text-brand-card 3xl:text-[2rem]">{name}</h3>
                <p className="text-sm text-brand-muted mb-3 3xl:text-xl">{role}</p>
                <p className="text-sm leading-relaxed text-brand-card 3xl:text-xl">{bio}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      <section className="border-t border-brand-card/10 px-6 py-20 sm:py-28">
        <div className="mx-auto lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px]">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-20 mb-12">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-brand-muted 3xl:text-base">
                Vell today
              </p>
              <h2 className="text-4xl sm:text-5xl font-bold leading-tight text-brand-card 3xl:text-[5rem]">
                Growing fast,<br />staying focused
              </h2>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="lg:pt-14"
            >
              <p className="text-lg leading-relaxed text-brand-card/70 3xl:text-2xl">
                Tens of thousands of people already use Vell to move money, stay
                connected abroad, and take control of their financial lives —
                with more joining every day.
              </p>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-brand-card/10 global-border-radius overflow-hidden border border-brand-card/10 mb-16"
          >
            {growthStats.map(({ value, label }) => (
              <motion.div
                key={label}
                variants={fadeUp}
                className="bg-brand-text px-6 py-8 3xl:px-8 3xl:py-10"
              >
                <p className="text-4xl font-bold text-brand-card 3xl:text-5xl">{value}</p>
                <p className="mt-1 text-sm text-brand-muted 3xl:text-2xl">{label}</p>
              </motion.div>
            ))}
          </motion.div>

          <div className="border-t border-brand-card/10 mb-16" />

          {/* CTA card */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-3xl bg-brand-bg px-8 py-12 sm:px-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8"
          >
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-brand-text 3xl:text-[2.5rem]">
                Join Vell — it&apos;s free
              </h3>
              <p className="mt-2 text-base text-brand-text/60 max-w-sm 3xl:text-2xl">
                One app. Every financial tool you need, wherever life takes you.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
              <button
                onClick={openPopup}
                className="inline-flex items-center gap-2 rounded-full border border-brand-grey/30 px-5 py-2.5 text-sm font-medium text-brand-text hover:bg-brand-text/10 transition-colors 3xl:text-xl"
              >
                Explore the app
              </button>
              <Link
                href="/register"
                className="inline-flex items-center gap-2 rounded-full bg-brand-grey px-5 py-2.5 text-sm font-semibold text-brand-card hover:bg-brand-muted transition-colors 3xl:text-xl"
              >
                Get started
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
